<template>
	<div class="waitlist-sheet">
		<ModalHeader :title="sheetTitle" :button-text="getString('add')" @click-button="addEntry">
			<template #extra-buttons>
				<AppLoader v-if="loadingData" :size="20" />
			</template>
		</ModalHeader>

		<div class="waitlist-empty" v-if="!roomsOrdersStore.waitlistCurrDate.length && !loadingData">
			<p>{{ getString('waitlist_empty') }}</p>
		</div>

		<div class="waitlist-queue" v-else>
			<div class="queue-entry" v-for="(entry, index) in waitingEntries" :key="entry.waitlist_id" @click="clickEntry(entry)">
				<div class="queue-position">{{ index + 1 }}</div>
				<div class="entry-time">
					<div class="time-main">{{ entry.timeDisplay }}</div>
					<div class="time-tolerance" v-if="entry.toleranceWindow">{{ entry.toleranceWindow }}</div>
				</div>
				<div class="entry-info">
					<div class="entry-name">{{ entry.name }} <span class="entry-people">x{{ entry.people }}</span></div>
					<div class="entry-contact" v-if="entry.phone || entry.email">{{ entry.phone ? entry.phone : entry.email }}</div>
					<div class="entry-fits">
						<span class="fits-dot" :class="entry.fits_now ? 'green' : 'grey'"></span>
						<span class="fits-text">{{ entry.fits_now ? getString('fits_now_at', [entry.fitsTimeDisplay]) : getString('no_spot_yet') }}</span>
					</div>
				</div>
				<div class="entry-actions" @click.stop>
					<div class="status-container" v-if="!entry.loadingUpdate">
						<div class="action-button" @click="promote(entry)">
							<div class="action-color green"></div>
							<div class="action-text">{{ getString('promote') }}</div>
						</div>
						<div class="action-button" @click="entryPopup = entry;$refs['waitlist-confirm-popup'].toggleStatus(true)">
							<div class="action-color danger"></div>
							<div class="action-text">{{ getString('cancel') }}</div>
						</div>
					</div>
					<div class="status-loading" v-else>
						<AppLoader :size="20" />
					</div>
				</div>
			</div>

			<div class="queue-divider" v-if="endedEntries.length" @click="endedExpanded = !endedExpanded">
				<p>{{ getString('no_longer_waiting') }} ({{ endedEntries.length }})</p>
				<AppChevronIcon :direction="endedExpanded ? 'up' : 'down'" />
			</div>

			<template v-if="endedExpanded">
				<div class="queue-entry ended" v-for="entry in endedEntries" :key="entry.waitlist_id">
					<div class="entry-time">
						<div class="time-main">{{ entry.timeDisplay }}</div>
						<div class="time-tolerance" v-if="entry.toleranceWindow">{{ entry.toleranceWindow }}</div>
					</div>
					<div class="entry-info">
						<div class="entry-name">{{ entry.name }} <span class="entry-people">x{{ entry.people }}</span></div>
						<div class="entry-contact" v-if="entry.phone || entry.email">{{ entry.phone ? entry.phone : entry.email }}</div>
						<div class="entry-status-label" :class="entry.status">{{ statusLabel(entry) }}</div>
					</div>
					<div class="entry-actions" @click.stop v-if="entry.status === 'canceled'">
						<div class="status-container" v-if="!entry.loadingUpdate">
							<div class="action-button" @click="updateStatus(entry, 'waiting')">
								<div class="action-color grey"></div>
								<div class="action-text">{{ getString('restore') }}</div>
							</div>
						</div>
						<div class="status-loading" v-else>
							<AppLoader :size="20" />
						</div>
					</div>
				</div>
			</template>
		</div>

		<AppPopup
			:title="getString('are_you_sure')"
			:body-text="getString('confirm_cancel_waitlist')"
			:buttons="popupButtons"
			ref="waitlist-confirm-popup"
		/>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useRoomsOrdersStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {getString: languageStore.getString, roomsOrdersStore, notificationStore, userStore};
	},
	name: "WaitlistSheet",
	components: {ModalHeader, AppLoader, AppPopup, AppChevronIcon},
	data() {
		return {
			entryPopup: null,
			loadingData: false,
			endedExpanded: false,
		}
	},
	computed: {
		popupButtons() {
			return [
				{text: this.getString('cancel'), callback: null, negative: true},
				{text: this.getString('confirm'), callback: () => {
					this.updateStatus(this.entryPopup, 'canceled');
					this.$refs['waitlist-confirm-popup'].toggleStatus(false);
				}},
			]
		},
		sheetTitle() {
			const waitingNum = this.roomsOrdersStore.waitlistWaitingCount;
			if (waitingNum === 1) {
				return this.getString('one_waiting');
			}
			return this.getString('waiting_x', [waitingNum]);
		},
		waitingEntries() {
			return this.roomsOrdersStore.waitlistCurrDate
				.filter(entry => entry.status === 'waiting')
				.sort((a, b) => a.fullDate - b.fullDate);
		},
		endedEntries() {
			return this.roomsOrdersStore.waitlistCurrDate
				.filter(entry => entry.status !== 'waiting')
				.sort((a, b) => a.fullDate - b.fullDate);
		},
	},
	methods: {
		statusLabel(item) {
			if(item.status === 'waiting') {
				return this.getString('waiting');
			} else if(item.status === 'fulfilled') {
				return this.getString('fulfilled');
			} else {
				return this.getString('canceled');
			}
		},
		addEntry() {
			this.roomsOrdersStore.manageWaitlistEntry = null;
			this.roomsOrdersStore.newWaitlistOpen = true;
		},
		clickEntry(item) {
			if(item.status !== 'waiting') {
				return;
			}
			this.roomsOrdersStore.manageWaitlistEntry = item;
			this.roomsOrdersStore.newWaitlistOpen = true;
		},
		promote(item) {
			this.roomsOrdersStore.newReservationPreset = {
				experienceId: item.experience_id,
				date: item.fullDate,
				time: item.fitsTimeFull ? item.fitsTimeFull : item.fullDate,
				name: item.name,
				email: item.email,
				phone: item.phone,
				people: item.people,
				children: item.children,
				dogs: item.dogs,
				notes: item.notes,
				waitlistId: item.waitlist_id,
			};
			this.roomsOrdersStore.newReservationTableId = true;
		},
		updateStatus(item, status) {
			item.loadingUpdate = true;
			this.roomsOrdersStore.updateWaitlistStatus(item.waitlist_id, status, (r) => {
				if(r && r.result && r.result === "OK") {
					this.roomsOrdersStore.getDayWaitlist(() => {
						item.loadingUpdate = false;
					});
				} else {
					this.notificationStore.showNotification("generic-error");
					item.loadingUpdate = false;
				}
			});
		},
	},
	mounted() {
		if(this.userStore.currBusinessId !== null) {
			this.loadingData = true;
			this.roomsOrdersStore.getDayWaitlist(() => {
				this.loadingData = false;
			});
		}
	},
}
</script>

<style scoped lang="scss">
.waitlist-empty {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 40px 0;

	p {
		font-size: 14px;
		color: var(--color-txt-2);
	}
}

.waitlist-queue {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.queue-divider {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 10px 4px 2px;
	cursor: pointer;

	p {
		font-size: 13px;
		color: var(--color-txt-2);
	}
}

.queue-entry {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 14px;
	border-radius: 13px;
	background-color: var(--color-bg-primary-2);
	box-shadow: $box_shadow_inset;
	cursor: pointer;

	&.ended {
		opacity: .6;
		cursor: default;
	}

	@media (max-width: 550px) {
		flex-wrap: wrap;

		.entry-actions {
			width: 100%;
		}
	}

	.queue-position {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background-color: var(--color-bg-primary-1);
		color: var(--color-text-primary);
		font-size: 14px;
		font-weight: 600;
		flex-shrink: 0;
	}

	.entry-time {
		display: flex;
		flex-direction: column;
		gap: 2px;
		width: 90px;
		flex-shrink: 0;

		.time-main {
			font-size: 18px;
			font-weight: 600;
			color: var(--color-text-primary);
		}

		.time-tolerance {
			font-size: 12px;
			color: var(--color-txt-2);
		}
	}

	.entry-info {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex-grow: 1;
		min-width: 0;

		.entry-name {
			font-size: 14px;
			color: var(--color-txt);

			.entry-people {
				color: var(--color-txt-2);
			}
		}

		.entry-contact {
			font-size: 14px;
			color: var(--color-txt-2);
		}

		.entry-fits {
			display: flex;
			align-items: center;
			gap: 6px;

			.fits-dot {
				width: 10px;
				height: 10px;
				border-radius: 50%;
				flex-shrink: 0;

				&.green {
					background-color: var(--color-secondary);
				}

				&.grey {
					background-color: var(--color-txt-2);
				}
			}

			.fits-text {
				font-size: 14px;
				color: var(--color-txt-2);
			}
		}

		.entry-status-label {
			font-size: 14px;

			&.fulfilled {
				color: var(--color-secondary);
			}

			&.canceled {
				color: var(--color-txt-2);
			}
		}
	}

	.entry-actions {
		flex-shrink: 0;

		.status-loading {
			display: flex;
			justify-content: center;
			align-items: center;
			min-width: 100px;
		}
	}

	.status-container {
		display: flex;
		justify-content: center;
		align-items: flex-start;
		flex-direction: column;
		border: 1px solid var(--color-primary);
		cursor: pointer;
		border-radius: 13px;
		min-width: 100px;

		.action-button {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			gap: 5px;
			border-bottom: 1px solid var(--color-primary);
			width: 100%;
			padding: 4px 12px;

			&:first-child {
				border-radius: 13px 13px 0 0;
			}

			&:last-child {
				border-radius: 0 0 13px 13px;
				border-bottom: none;
			}
		}

		.action-text {
			font-size: 14px;
			color: var(--color-text-primary);
		}

		.action-color {
			width: 10px;
			height: 10px;
			border-radius: 50%;

			&.green {
				background-color: var(--color-secondary);
			}

			&.danger {
				background-color: var(--color-danger);
			}

			&.grey {
				background-color: var(--color-txt-2);
			}
		}
	}
}
</style>
